import { getUpdateTypeLabel, isPackageUpdate } from "@/domain/issue"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"

import type { ListQuickWinsResponse } from "@/api/useQuickWins.types"
import type { QuickWin, QuickWinType } from "@/domain/issue"

export const getQuickWinTitleKey = (type: QuickWinType) =>
  isPackageUpdate(type)
    ? "quickWins.card.titlePackageUpdate"
    : "quickWins.card.titleBaseImageUpdate"

export const formatVersionPath = (current: string, fix: string) =>
  `${current} \u2192 ${fix}`

export const buildQuickWinCopyText = (
  quickWin: {
    type: QuickWinType
    dependencyName: string
    currentVersion: string
    fixVersion: string
    sourceFilePath: string
    totalIssues: number
    severityCounts: [string, number][]
  },
  projectName: string | undefined,
  t: (key: string, params?: Record<string, unknown>) => string
): string => {
  const isPackage = isPackageUpdate(quickWin.type)
  const fixLine = isPackage
    ? `${quickWin.dependencyName}:${quickWin.currentVersion} -> ${quickWin.fixVersion}`
    : `${quickWin.dependencyName} -> ${quickWin.fixVersion}`

  const severityLabel = quickWin.severityCounts
    .map(
      ([sev, count]) => `${count} ${t(`vulnerabilities.${sev.toLowerCase()}`)}`
    )
    .join(", ")
  const resolveLine = `${quickWin.totalIssues} Issues (${severityLabel})`

  const lines = [
    t("general.kodemRemediation"),
    "",
    `${t("quickWins.copy.project")}:`,
    projectName || "",
    "",
    isPackage
      ? `${t("inventory.images.drawer.issue.updatePackage", { count: 1 })}:`
      : `${t("inventory.images.drawer.issue.updateBaseImage", { count: 1 })}:`
  ]
  if (isPackage && quickWin.sourceFilePath) {
    lines.push(`${t("quickWins.card.sourceLabel")}:`)
    lines.push(quickWin.sourceFilePath)
  }
  lines.push(fixLine, "", `${t("quickWins.copy.toResolve")}:`, resolveLine)
  return lines.join("\n")
}

const mapQuickWinRow = (item: QuickWin) => ({
  ...item,
  key: item.id,
  titleKey: getQuickWinTitleKey(item.type),
  titleParams: {
    prefix: getUpdateTypeLabel(item.updateType),
    count: item.totalIssues
  },
  updateTypeLabel: getUpdateTypeLabel(item.updateType),
  versionPath: formatVersionPath(item.currentVersion, item.fixVersion),
  severityCounts: getIssuesCountMapOrderedSeverity(item.issuesSummary)
})

export type QuickWinRow = ReturnType<typeof mapQuickWinRow>

export const getQuickWinsSelector = (response: ListQuickWinsResponse) => {
  const { data, ...rest } = response || {}

  if (!data) return response as ListQuickWinsResponse & { data: undefined }

  const mappedData = data.map(mapQuickWinRow)

  return {
    data: mappedData,
    totalIssuesSum: mappedData.reduce((sum, qw) => sum + qw.totalIssues, 0),
    ...rest
  }
}
